/**
 * Primer uso — DESKTOP: los cuatro pasos a la izquierda, el paso activo a la derecha.
 *
 * La diferencia real con la variante mobile: **se ve todo el recorrido de una vez** y se puede
 * saltar a un paso ya hecho para corregirlo. En un teléfono eso no cabe y obliga a un flujo lineal;
 * en el PC, esconder el mapa del proceso sería desperdiciar el ancho.
 *
 * Y en el paso de rutina, las columnas de días se ven en paralelo con el catálogo al lado, que es
 * como se arma una rutina de verdad: comparando días, no de uno en uno.
 */

import { type ReactNode } from 'react';
import { fechaLarga, nombreMusculo, type Sexo } from '@mg/core';

import { useOnboarding } from '../features/useOnboarding.ts';
import { Aviso, Campo, Icono, Insignia, Selector, Vacio } from '../ui/componentes.tsx';
import type { PropsBienvenida } from './Bienvenida.tsx';

export function BienvenidaDesktop({ alTerminar, onSalir }: PropsBienvenida): ReactNode {
  const o = useOnboarding(alTerminar);

  if (o.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (o.esperandoPlanilla) return <Preparando o={o} onSalir={onSalir} />;
  if (o.paso === 'listo') return <Listo o={o} />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr)', minHeight: '100%' }}>
      <aside
        style={{
          padding: 'var(--e6) var(--e4)',
          borderRight: '1px solid var(--hierro-borde)',
          background: 'var(--hierro-hundido)',
        }}
      >
        <div
          className="d-marca"
          style={{ paddingLeft: 0, justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span className="fila" style={{ gap: 'var(--e2)', alignItems: 'baseline' }}>
            <span className="titulo" style={{ letterSpacing: '-0.04em' }}>
              mg
            </span>
            <span className="nota--fina">configurar</span>
          </span>
          <button type="button" className="boton boton--chico boton--fantasma" onClick={() => void onSalir()}>
            Salir de la cuenta
          </button>
        </div>

        <p className="nota" style={{ marginBottom: 'var(--e5)' }}>
          Cuatro pasos y podés registrar tu primera serie. Todo se puede cambiar después.
        </p>

        <div className="columna" style={{ gap: 'var(--e2)' }}>
          {o.pasos.map((p, i) => {
            const hecho = i < o.indice;
            const activo = i === o.indice;
            return (
              <button
                key={p.id}
                type="button"
                className="d-nav-item"
                onClick={() => (hecho ? o.setPaso(p.id) : undefined)}
                disabled={!hecho && !activo}
                style={{
                  minHeight: 56,
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  gap: 2,
                  padding: 'var(--e2) var(--e3)',
                  background: activo ? 'var(--ambar-tenue)' : undefined,
                  color: activo ? 'var(--ambar)' : hecho ? 'var(--tiza)' : 'var(--tiza-baja)',
                  cursor: hecho ? 'pointer' : 'default',
                }}
              >
                <span className="fila" style={{ gap: 6 }}>
                  {hecho ? <Icono nombre="check" tamano={14} /> : <span className="lectura">{i + 1}</span>}
                  <strong style={{ fontSize: 14 }}>{p.titulo}</strong>
                </span>
                <span className="nota--fina" style={{ textAlign: 'left' }}>
                  {p.para}
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <main style={{ padding: 'var(--e6)', maxWidth: 1100 }}>
        <h1 className="titulo" style={{ fontSize: 28 }}>
          {o.pasos[o.indice]?.titulo}
        </h1>
        <p className="nota" style={{ marginTop: 4, marginBottom: 'var(--e5)' }}>
          {o.pasos[o.indice]?.para}
        </p>

        {o.paso === 'perfil' && <PasoPerfil o={o} />}
        {o.paso === 'gimnasio' && <PasoGimnasio o={o} />}
        {o.paso === 'rutina' && <PasoRutina o={o} />}
        {o.paso === 'plan' && <PasoPlan o={o} />}

        {o.error && (
          <div style={{ marginTop: 'var(--e4)', maxWidth: 700 }}>
            <Aviso tono="atencion">
              {o.error}
              {o.puedeReintentar && (
                <button
                  type="button"
                  className="boton boton--chico"
                  style={{ marginTop: 'var(--e2)' }}
                  disabled={o.guardando}
                  onClick={o.reintentar}
                >
                  {o.guardando ? 'Subiendo…' : 'Reintentar'}
                </button>
              )}
            </Aviso>
          </div>
        )}

        <div className="fila" style={{ gap: 'var(--e3)', marginTop: 'var(--e6)', alignItems: 'center' }}>
          {o.indice > 0 && (
            <button type="button" className="boton" onClick={o.anterior}>
              <Icono nombre="atras" tamano={16} />
              Atrás
            </button>
          )}
          <button
            type="button"
            className="boton boton--principal"
            disabled={!o.puedeSeguir || o.guardando}
            onClick={o.siguiente}
          >
            {o.guardando ? 'Guardando…' : o.paso === 'plan' ? 'Listo, empezar' : 'Seguir'}
          </button>
          {o.queFalta && <span className="nota--fina">{o.queFalta}</span>}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Con sesión pero sin planilla todavía. En el PC hay ancho para explicar el modelo entero mientras
 * se espera: es la única vez que la app exige conexión, y entender por qué evita el "se colgó".
 */
function Preparando({
  o,
  onSalir,
}: {
  o: ReturnType<typeof useOnboarding>;
  onSalir: () => Promise<void>;
}): ReactNode {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100%', padding: 'var(--e6)' }}>
      <div className="d-rejilla d-rejilla--2" style={{ maxWidth: 900, alignItems: 'start' }}>
        <section>
          <span className="eyebrow">Tu cuenta</span>
          <h1 className="titulo" style={{ fontSize: 28, marginTop: 'var(--e2)' }}>
            Preparando tu planilla…
          </h1>
          <p className="nota" style={{ marginTop: 'var(--e3)' }}>
            Estamos bajando tus datos. Es lo único de la app que necesita conexión sí o sí, y pasa
            una vez por dispositivo.
          </p>

          {o.errorPlanilla && (
            <div style={{ marginTop: 'var(--e4)' }}>
              <Aviso tono="atencion">{o.errorPlanilla}</Aviso>
            </div>
          )}

          <div className="fila" style={{ gap: 'var(--e3)', marginTop: 'var(--e5)' }}>
            <button
              type="button"
              className="boton boton--principal"
              disabled={o.sincronizando}
              onClick={() => void o.reintentarPlanilla()}
            >
              {o.sincronizando ? 'Buscando…' : 'Reintentar'}
            </button>
            <button type="button" className="boton boton--fantasma" onClick={() => void onSalir()}>
              Salir de la cuenta
            </button>
          </div>
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Por qué tarda</span>
          <p className="nota" style={{ marginTop: 'var(--e2)' }}>
            Cada usuario tiene su propia planilla de Google, separada de la de cualquier otra
            persona. La app se la baja entera a este dispositivo y desde ahí calcula todo sin volver
            a preguntar: por eso después funciona con el celular en modo avión.
          </p>
          <p className="nota" style={{ marginTop: 'var(--e3)' }}>
            Si acabás de crear la cuenta, la planilla se está creando ahora. Si no aparece, es que
            no hay conexión con el servidor.
          </p>
        </section>
      </div>
    </div>
  );
}

/**
 * El último paso. Existe para no soltar a nadie en Inicio sin decirle dónde se registra la primera
 * serie: es la pregunta que todo el onboarding estuvo preparando y que hasta acá nadie contestó.
 */
function Listo({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100%', padding: 'var(--e6)' }}>
      <div style={{ maxWidth: 900, width: '100%' }}>
        <span className="eyebrow">Configuración terminada</span>
        <h1 className="titulo" style={{ fontSize: 32, marginTop: 'var(--e2)' }}>
          Listo
        </h1>
        <p className="nota" style={{ marginTop: 'var(--e3)' }}>
          Tu gimnasio y tu rutina quedaron guardados. Nada de esto queda fijo: se cambia cuando
          quieras.
        </p>

        <div className="d-rejilla d-rejilla--3" style={{ marginTop: 'var(--e5)', alignItems: 'start' }}>
          <section className="tarjeta">
            <span className="eyebrow">Lo primero</span>
            <p className="nota" style={{ marginTop: 'var(--e2)' }}>
              <strong>Tu primera serie se registra en Entrenar</strong>: abrís el día, empezás la
              sesión y el ＋ de cada ejercicio guarda una serie igual a la anterior.
            </p>
          </section>
          <section className="tarjeta">
            <span className="eyebrow">Qué toca hoy</span>
            <p className="nota" style={{ marginTop: 'var(--e2)' }}>
              Inicio responde eso solo: qué día de la rutina sigue, o si ya entrenaste.
            </p>
          </section>
          <section className="tarjeta">
            <span className="eyebrow">La otra mitad</span>
            <p className="nota" style={{ marginTop: 'var(--e2)' }}>
              Con tres mediciones de la balanza la app puede empezar a decir cuánto de lo que
              bajaste era grasa. Con menos, no alcanza para responder nada.
            </p>
          </section>
        </div>

        <button
          type="button"
          className="boton boton--principal"
          style={{ marginTop: 'var(--e6)' }}
          onClick={o.cerrar}
        >
          Empezar
        </button>
      </div>
    </div>
  );
}

function PasoPerfil({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <div className="d-rejilla d-rejilla--2" style={{ maxWidth: 700, alignItems: 'start' }}>
      <Campo etiqueta="Tu nombre" texto value={o.nombre} onChange={(e) => o.setNombre(e.target.value)} />

      <Campo
        etiqueta="Altura (cm)"
        inputMode="numeric"
        value={o.altura}
        onChange={(e) => o.setAltura(e.target.value)}
        ayuda="Se pide una sola vez. Si no la sabés, seguí y la completás después."
      />

      <Selector etiqueta="Sexo" value={o.sexo} onChange={(e) => o.setSexo(e.target.value as Sexo)}>
        <option value="M">Masculino</option>
        <option value="F">Femenino</option>
        <option value="otro">Prefiero no decirlo</option>
      </Selector>

      <Selector
        etiqueta="Unidad preferida"
        value={o.unidad}
        onChange={(e) => o.setUnidad(e.target.value as 'kg' | 'lb')}
      >
        <option value="kg">Kilos</option>
        <option value="lb">Libras</option>
      </Selector>

      {o.alturaSugerida && (
        <div style={{ gridColumn: '1 / -1' }}>
          <Aviso tono="info">
            Tu balanza reporta IMC, así que la altura se puede despejar: {o.alturaSugerida} cm.
            <button
              type="button"
              className="boton boton--chico"
              style={{ marginTop: 8 }}
              onClick={() => o.setAltura(String(o.alturaSugerida))}
            >
              Usar {o.alturaSugerida} cm
            </button>
          </Aviso>
        </div>
      )}

      <p className="nota--fina" style={{ gridColumn: '1 / -1' }}>
        La unidad es sólo para mostrar. La app guarda el peso tal como lo leés en la placa y muestra
        las dos unidades, así que da igual si las máquinas están en libras y tu balanza en kilos.
      </p>
    </div>
  );
}

function PasoGimnasio({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <div className="d-rejilla d-rejilla--2" style={{ maxWidth: 860, alignItems: 'start' }}>
      <section className="tarjeta">
        <span className="eyebrow">Un gimnasio nuevo</span>
        <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
          <Campo
            etiqueta="Nombre"
            texto
            placeholder="Ej. Smart Fit Providencia"
            value={o.nombreGimnasio}
            onChange={(e) => {
              o.setNombreGimnasio(e.target.value);
              o.setGimnasioExistente(null);
            }}
          />

          <div>
            <span className="eyebrow etiqueta">¿Qué días cierra?</span>
            <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
              {o.diasSemana.map((d) => (
                <button
                  key={d.n}
                  type="button"
                  className={
                    o.diasCierre.includes(d.n) ? 'boton boton--chico boton--principal' : 'boton boton--chico'
                  }
                  onClick={() => o.alternarDia(d.n)}
                >
                  {d.etiqueta}
                </button>
              ))}
            </div>
            <p className="nota--fina" style={{ marginTop: 6 }}>
              Estos días no cuentan como disponibles al calcular tu plan.
            </p>
          </div>
        </div>
      </section>

      <section className="tarjeta">
        <span className="eyebrow">O uno que ya está cargado</span>
        {o.gimnasiosDisponibles.length === 0 ? (
          <Vacio>Todavía no hay ninguno.</Vacio>
        ) : (
          <>
            <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
              {o.gimnasiosDisponibles.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={o.gimnasioExistente === g.id ? 'boton boton--principal' : 'boton'}
                  onClick={() => {
                    o.setGimnasioExistente(o.gimnasioExistente === g.id ? null : g.id);
                    o.setNombreGimnasio('');
                  }}
                  style={{ justifyContent: 'flex-start' }}
                >
                  {g.nombre}
                </button>
              ))}
            </div>
            <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
              Eligiendo uno existente heredás su mapa de máquinas: qué aparato está en cada
              ubicación, y de cuánto en cuánto sube cada placa.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

function PasoRutina({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  if (o.plantillaId === null && o.dias.length === 0) {
    return (
      <>
        <div className="d-rejilla d-rejilla--2" style={{ maxWidth: 900 }}>
          {o.plantillas.map((p) => (
            <button
              key={p.id}
              type="button"
              className="tarjeta"
              onClick={() => o.elegirPlantilla(p)}
              style={{ textAlign: 'left', color: 'inherit', cursor: 'pointer' }}
            >
              <div className="fila fila--entre">
                <strong style={{ fontSize: 16 }}>{p.nombre}</strong>
                <Insignia>
                  {p.dias.length} {p.dias.length === 1 ? 'día' : 'días'}
                </Insignia>
              </div>
              <p className="nota--fina" style={{ marginTop: 4 }}>
                {p.descripcion}
              </p>
              <ul className="nota--fina" style={{ margin: '10px 0 0', paddingLeft: '1.1em' }}>
                {p.dias.map((d) => (
                  <li key={d.nombre}>
                    {d.nombre} · {d.ejercicios.length} ejercicios
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        <button type="button" className="boton" style={{ marginTop: 'var(--e4)' }} onClick={() => o.elegirPlantilla(null)}>
          Armarla desde cero
        </button>
      </>
    );
  }

  return (
    <>
      <div className="fila fila--entre" style={{ marginBottom: 'var(--e4)' }}>
        <div className="fila" style={{ gap: 'var(--e3)' }}>
          <Campo
            etiqueta="Series"
            inputMode="numeric"
            value={String(o.seriesObjetivo)}
            onChange={(e) => o.setSeriesObjetivo(Math.max(1, Number(e.target.value) || 1))}
          />
          <Campo
            etiqueta="Reps mín"
            inputMode="numeric"
            value={String(o.repsMin)}
            onChange={(e) => o.setRepsMin(Math.max(1, Number(e.target.value) || 1))}
          />
          <Campo
            etiqueta="Reps máx"
            inputMode="numeric"
            value={String(o.repsMax)}
            onChange={(e) => o.setRepsMax(Math.max(o.repsMin, Number(e.target.value) || o.repsMin))}
          />
        </div>
        <div style={{ textAlign: 'right', maxWidth: 340 }}>
          <button type="button" className="boton boton--chico boton--fantasma" onClick={() => o.elegirPlantilla(null)}>
            Cambiar plantilla
          </button>
          <p className="nota--fina" style={{ marginTop: 6 }}>
            El rango de reps es lo que usa la app para sugerirte cuándo subir la placa: cerrás todas
            las series en el tope, subís un escalón.
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(o.dias.length + 1, 4)}, minmax(0, 1fr))`,
          gap: 'var(--e4)',
          alignItems: 'start',
        }}
      >
        {o.dias.map((d, i) => (
          <section key={i} className="tarjeta">
            <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
              <input
                className="campo campo--texto"
                style={{ minHeight: 36, fontWeight: 600, fontSize: 14 }}
                value={d.nombre}
                onChange={(e) => o.renombrarDia(i, e.target.value)}
                aria-label={`Nombre del día ${i + 1}`}
              />
              {o.dias.length > 1 && (
                <button
                  type="button"
                  className="boton boton--chico boton--fantasma"
                  style={{ minHeight: 36, padding: '0 8px' }}
                  onClick={() => o.quitarDia(i)}
                  aria-label={`Quitar ${d.nombre}`}
                >
                  <Icono nombre="cerrar" tamano={14} />
                </button>
              )}
            </div>

            <div className="columna" style={{ gap: 4 }}>
              {d.ejercicios.map((id) => {
                const e = o.catalogoPorId.get(id);
                return (
                  <div key={id} className="fila fila--entre">
                    <span style={{ fontSize: 13 }}>
                      {e?.nombre ?? id}
                      {e && (
                        <span className="nota--fina"> · {nombreMusculo(e.musculo_primario)}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="boton boton--chico boton--fantasma"
                      style={{ minHeight: 28, padding: '0 6px' }}
                      onClick={() => o.quitarEjercicio(i, id)}
                      aria-label={`Quitar ${e?.nombre ?? id}`}
                    >
                      <Icono nombre="cerrar" tamano={12} />
                    </button>
                  </div>
                );
              })}
              {d.ejercicios.length === 0 && (
                <p className="nota--fina" style={{ color: 'var(--ambar)' }}>
                  Sin ejercicios todavía.
                </p>
              )}
            </div>

            <div style={{ marginTop: 'var(--e3)' }}>
              <Selector
                etiqueta="Agregar"
                value=""
                onChange={(e) => {
                  if (e.target.value) o.agregarEjercicio(i, e.target.value);
                }}
              >
                <option value="">Elegí un ejercicio…</option>
                {o.catalogo
                  .filter((e) => !d.ejercicios.includes(e.id))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
              </Selector>
            </div>
          </section>
        ))}

        {o.dias.length < 7 && (
          <button
            type="button"
            className="tarjeta"
            onClick={o.agregarDia}
            style={{
              borderStyle: 'dashed',
              color: 'var(--tiza-media)',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              minHeight: 120,
              gap: 6,
            }}
          >
            <Icono nombre="mas" tamano={22} />
            Agregar un día
          </button>
        )}
      </div>
    </>
  );
}

function PasoPlan({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <div className="d-rejilla d-rejilla--2" style={{ maxWidth: 860, alignItems: 'start' }}>
      <section className="tarjeta">
        <div className="fila" style={{ gap: 'var(--e2)' }}>
          <button
            type="button"
            className={o.conPlan ? 'boton boton--principal crece' : 'boton crece'}
            onClick={() => o.setConPlan(true)}
          >
            Compré sesiones
          </button>
          <button
            type="button"
            className={!o.conPlan ? 'boton boton--principal crece' : 'boton crece'}
            onClick={() => o.setConPlan(false)}
          >
            Voy libre
          </button>
        </div>

        {o.conPlan ? (
          <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e4)' }}>
            <Campo
              etiqueta="Cuántas sesiones"
              inputMode="numeric"
              value={o.sesionesTotales}
              onChange={(e) => o.setSesionesTotales(e.target.value)}
            />
            <Campo
              etiqueta="Cuándo vencen"
              texto
              type="date"
              value={o.vencimiento}
              min={o.datos.hoy}
              onChange={(e) => o.setVencimiento(e.target.value)}
              {...(o.vencimiento > o.datos.hoy ? { ayuda: `Vence el ${fechaLarga(o.vencimiento)}.` } : {})}
            />
          </div>
        ) : (
          <p className="nota" style={{ marginTop: 'var(--e4)' }}>
            Sin plan la app registra todo igual. Podés cargar uno después, cuando compres un paquete.
          </p>
        )}
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Para qué sirve</span>
        <p className="nota" style={{ marginTop: 'var(--e2)' }}>
          Es lo que ninguna app comercial resuelve: con las sesiones, el vencimiento, los días que
          cierra el gimnasio y tus viajes, la app calcula cuántos <strong>días de colchón</strong> te
          quedan.
        </p>
        <p className="nota" style={{ marginTop: 'var(--e3)' }}>
          Te avisa cuando el margen baja de 2 días, cuando hay sesiones que van a sobrar al llegar la
          fecha límite, y cuando algo cambia te dice qué cambió:{' '}
          <em>"perdiste el jueves → el colchón pasó de 4 a 3 días"</em>.
        </p>
      </section>
    </div>
  );
}
